import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/en-gb';

import { useMemo, useState } from 'react';

import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import styles from './CalendarPage.module.css';
import { useUser } from '../../../Context/useUser';
import { useEffect } from 'react';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import Utils from '../../../Utils';
import { workoutToCalendarEvent, raceToCalendarEvent } from './scheduling';
import CreateWorkout from './CreateWorkout';
import UpdateWorkout from './UpdateWorkout';
import CreateChoice from './CreateChoice';
import CreateRace from './CreateRace';
import UpdateRace from './UpdateRace';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import ShowRecap from './ShowRecap';
import { FaFlagCheckered, FaSwimmer } from 'react-icons/fa';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { GoStopwatch } from "react-icons/go";
moment.locale('en-gb');

const localizer = momentLocalizer(moment);

const typeToForm = {
    workout_CREATE: CreateWorkout,
    race_CREATE: CreateRace,
    workout_UPDATE: UpdateWorkout,
    race_UPDATE: UpdateRace,
    recap_UPDATE: ShowRecap,
    CREATE: CreateChoice
};

const EventComponent = ({ event }) => {
    let icon;
    switch (event.type) {
        case 'race':
            icon = <FaFlagCheckered />;
            break;
        case 'workout':
            icon = <FaSwimmer />;
            break;
        case 'recap':
            icon = <AiOutlinePlusCircle />;
            break;
        case 'test':
            icon = <GoStopwatch />;
            break;
        default:
            icon = null;
    }
    return (
        <div className={styles.eventContent}>
            {icon}
            <span>{event.title}</span>
        </div>
    );
};

export default function CalendarPage () {
    const { user } = useUser();
    const { athleteId } = useParams();
    const visitedUser = useVisitingUser(athleteId);
    const currentUser = athleteId ? visitedUser : user;

    const [events, setEvents] = useState(null);
    const [onCreateEventModal, setOnCreateEventModal] = useState(null);

    const fetchEvents = async user => {
        if (!user) return;
        const [{ data: workouts }, { data: races }, { data: tests }] = await Promise.all([
            API.getUserWorkouts(user._id),
            API.getRacesByUserId(user._id),
            API.getAllTestsForUser(user._id)
        ]);

        const allEvents = [
            ...workouts.map(workoutToCalendarEvent),
            ...races.map(raceToCalendarEvent),
            ...tests.map(event => ({
                ...event,
                title: `Test ${event.type} ${new Date(event.testDate).toLocaleDateString('el-gr')}`,
                type: 'test',
                start: new Date(event.testDate),
                end: new Date(event.testDate),
                testDate: Utils.dateToSimpleDateFormat(new Date(event.testDate))
            }))
        ];

        setEvents(allEvents);
    };

    useEffect(() => {
        fetchEvents(currentUser);
    }, [currentUser]);

    const onEventDrop = data => {
        const {
            start,
            end,
            event: { id }
        } = data;
        setEvents(currentEvents => {
            const currEvent = currentEvents.find(ev => ev.id === id);
            currEvent.start = start;
            currEvent.end = end;
            return [...currentEvents];
        });
    };

    const onSuccess = async (message, data) => {
        toast.success(message, {
            position: 'bottom-center'
        });
        setEvents([]);
        fetchEvents(currentUser);
        setOnCreateEventModal(null);
    };

    const onFailure = message => {
        toast.error(message, {
            position: 'bottom-center'
        });
    };

    const onSelectSlot = data => {
        data.type = `CREATE`;
        setOnCreateEventModal(data);
    };

    const onSelecting = data => {
        data.type = `${data.type}_UPDATE`;
        setOnCreateEventModal(data);
    };

    const onEventChosen = data => {
        data.type = `${data.type}_CREATE`;
        setOnCreateEventModal(data);
    };

    const CreateEventModal = () => {
        if (!onCreateEventModal) return null;
        const Modal = typeToForm[onCreateEventModal.type];
        if (!Modal) return null;

        return (
            <Modal
                user={currentUser}
                data={onCreateEventModal}
                athleteCSS={onCreateEventModal?.css ?? currentUser?.currentCSS}
                athleteMSS={onCreateEventModal?.mss ?? currentUser?.currentMSS}
                closeCB={() => {
                    setEvents([]);
                    fetchEvents(currentUser);
                    setOnCreateEventModal(false);
                }}
                onFailure={onFailure}
                onSuccess={onCreateEventModal.type === 'CREATE' ? onEventChosen : onSuccess}
            />
        );
    };

    const eventPropGetter = (event, start, end, isSelected) => {
        if (event.type === 'workout') {
            return {
                className: `${styles[`calendarType-${event.type}`]} ${styles[
                    `calendarType-${event.type}-${event.status?.toLowerCase() || 'assigned'}`
                ]
                    }`
            };
        }
        if (event.type === 'race') {
            return {
                className: `${styles[`calendarType-${event.type}`]} ${styles[
                    `calendarType-${event.type}-${event.status?.toLowerCase() || 'assigned'}`
                ]
                    }`
            };
        }

        return {
            className: styles[`calendarType-${event.type}`]
        };
    };

    const sEvents = useMemo(() => {
        if (!events) return [];

        const res = {};

        for (const event of events) {
            const actualDate = new Date(
                event.type === 'workout' ? event.workoutStart : event.start
            );
            const dayOfWeek = actualDate.getDay();
            const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
            actualDate.setDate(actualDate.getDate() + daysUntilSunday);
            const key = actualDate.toDateString();
            if (!res[key]) {
                res[key] = {
                    start: new Date(key),
                    end: new Date(key),
                    title: `Weekly Recap`,
                    type: 'recap',
                    scheduled: 0,
                    completed: 0,
                    events: []
                };
            }
            res[key].events.push(event);
            if (event.type === 'workout') {
                res[key].scheduled += 1;
                if (event.atl) res[key].completed += 1;
            }
            if (event.type === 'race') {
                res[key].scheduled += 1;
                if (event.status === 'Completed') res[key].completed += 1;
            }
            if (event.type === 'test') {
                res[key].completed += 1;
            }
        }
        return Object.values(res);
    }, [events]);

    if (!events) return null;

    return (
        <>
            <Calendar
                className='island'
                defaultView='month'
                events={[...events, ...sEvents]}
                onSelectEvent={onSelecting}
                localizer={localizer}
                onEventDrop={onEventDrop}
                onSelectSlot={onSelectSlot}
                selectable
                eventPropGetter={eventPropGetter}
                resizable={false}
                // firstDayOfWeek={1}
                components={{
                    event: EventComponent
                }}
            />
            <CreateEventModal />
        </>
    );
}
